import { BehaviorSubject } from 'rxjs';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { GamificationMainService } from './gamification-main.service';
import { GivenManualAward, ManualAward } from '../models/manual-award.model';
import { Injectable } from '@angular/core';
import { ProfileDTO } from 'src/app/admin/user/user-admin.model';

@Injectable({
  providedIn: 'root'
})
export class ManualAwardsService {
  private loadingManualAwardSubject = new BehaviorSubject<boolean>(false);
  data: any = {
    manualAwards: [],
    awardedToday: [],
    awardsFolderStates: {},
    manualAwardsHistory: [],
    manualAwardsByFolders: [],
    localizedValidationMessages: null, //Localization messages relating to validation
  }
  loadingManualAward = this.loadingManualAwardSubject.asObservable();

  template() {
    let newTemplate = {
      Name: "",
      Folder: "",
      Description: "",
      BadgeID: "",
      Coins: 0,
      XP: 0,
      AssignedGroups: [],
      RuleID: 0,
      AwardLimitValue: 0,
      AwardLimitPeriod: "",
      IsUnlimited: false,
      StartDate: null,
      EndDate: null
    }

    return newTemplate;
  }
  constructor(
    private cvApiService: CVApiService,
    private gamificationMainService: GamificationMainService,
    public cvConfigurationService: CvConfigurationService
  ) { }

  makeNewManualAward(): any {
    return this.template();
  };

  setLoadingAwardValue = (isLoading: boolean) => {
    this.loadingManualAwardSubject.next(isLoading);
  }

  async loadManualAwards(forceLoad?: boolean) {
    if (this.loadingManualAwardSubject.getValue() && !forceLoad) return null; // we don't nee to load manual awards if the request is running
    try {
      this.setLoadingAwardValue(true);
      const url = "/api/v1/gamification/manualawards";
      let response: any = await this.cvApiService.get(url);

      if (response.status_code == 200) {
        this.data.manualAwardsByFolders = response.data.ManualAwards;
        this.data.manualAwards = this.data.manualAwardsByFolders.reduce((awards, awardFolder) => {
          Array.prototype.push.apply(awards, awardFolder.Value);
          return awards;
        }, []);

        return response;
      } else {
        throw response;
      }

    } catch (error) {
      showErrorMessage("Error loading manual awards: " + error.message);
    } finally {
      this.setLoadingAwardValue(false);
    }
  }

  async loadAllManualAwardHistory() {
    try {
      const url = "/api/v1/gamification/manualawards/history";
      let response = await this.cvApiService.get(url);

      if (response.status_code == 200) {
        this.data.manualAwardsHistory = response.data;

        // Generate a list of awards given today
        var todayString = (new Date(Date.now())).toLocaleDateString();
        this.data.awardedToday = this.data.manualAwardsHistory.filter((m) => {
          var dateGiven = new Date(m.DateGiven);
          return todayString === dateGiven.toLocaleDateString();
        });

        this.countManualAwardsByFilter();
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage("Error loading manual award history: " + error.message);
    }
  }

  countManualAwardsByFilter() {
    var loginIdCache = {}; // Keep track of indexOf results to reduce array iterations

    var filteredAwardCount = this.data.manualAwardsHistory.reduce((dict, historyItem) => {
      if (loginIdCache[historyItem.RecipientID] === undefined) {
        loginIdCache[historyItem.RecipientID] = this.gamificationMainService.data.selectedLogins.indexOf(historyItem.RecipientID) >= 0;
      }

      if (loginIdCache[historyItem.RecipientID] === false) {
        return dict;
      }

      if (dict[historyItem.AwardID] === undefined) {
        dict[historyItem.AwardID] = 0;
      }

      dict[historyItem.AwardID]++;
      return dict;
    }, {});

    this.data.manualAwards.forEach((award) => {
      award.FilteredAwardCount = 0;
      if (filteredAwardCount[award.RuleID] != undefined)
        award.FilteredAwardCount = filteredAwardCount[award.RuleID];
    });

    return filteredAwardCount;
  }

  async loadManualAwardHistory(manualAward: ManualAward) {
    try {
      const url = `/api/v1/gamification/manualawards/${manualAward.RuleID}/history`;
      let response = await this.cvApiService.get(url);

      if (response.status_code == 200) {
        return response.data;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  async loadAllAwardHistory(from: number, to: number) {
    try {
      const url = `/api/v1/gamification/manualawards/history/${from}/${to}`;
      let response = await this.cvApiService.get(url);

      if (response.status_code == 200) {
        return response.data;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  async saveManualAward(manualAward: ManualAward) {
    try {
      const url = `/api/v1/gamification/manualawards?isCXone=${this.cvConfigurationService.isCxOne}`;
      let response = await this.cvApiService.post(url, manualAward);

      if (response.status_code == 200) {
        return response.data;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  async deactivateManualAward(manualAward: ManualAward) {
    try {
      const url = `/api/v1/gamification/manualawards/${manualAward.RuleID}/deactivate`;
      let response = await this.cvApiService.post(url, manualAward);
      if (response.status_code == 200) {
        return response.data;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  async giveOutAwards(manualAward: ManualAward | any, giveToLogins: any) {
    try {
      const url = `/api/v1/gamification/manualawards/${manualAward.RuleID}/give`;
      let response = await this.cvApiService.post(url, giveToLogins);
      if (response.status_code == 200) {
        return response.data;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  async giveManualAwards(ruleID: number, giveToLogins: any) {
      try {
          const url = `/api/v2/gamification/manualawards/${ruleID}/give?isCXone=${this.cvConfigurationService.isCxOne}`;
          let response = await this.cvApiService.post(url, giveToLogins);
          if (response.status_code == 200) {
              return response.data;
          } else {
              throw response;
          }
      } catch (error) {
          showErrorMessage(error.message);
      }
  };

  async getLoginsForManualAward(RuleID: number) {
    try {
      const url = `/api/v1/gamification/manualawards/${RuleID}/logins`;
      let response = await this.cvApiService.get(url);

      if (response.status_code == 200) {
        return response;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  async getManualAwardLogins(RuleID: number) {
    try {
      const url = `/api/v2/gamification/manualawards/${RuleID}/logins`;
      let response = await this.cvApiService.get(url);

      if (response.status_code == 200) {
        return response;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  getValidationMessage(key: string, fallback: string): string {
    if (
      this.data == null ||
      this.data.localizedValidationMessages == null ||
      this.data.localizedValidationMessages[key] == null
    )
      return fallback;
    if (
      Array.isArray(this.data.localizedValidationMessages[key]) &&
      this.data.localizedValidationMessages[key].length > 0
    )
      return this.data.localizedValidationMessages[key][0];
    return this.data.localizedValidationMessages[key];
  }

  async removeManualAward(manualAward: GivenManualAward) {
    try {
      const url = `/api/v1/gamification/manualawards/remove`;
      let response = await this.cvApiService.post(url, manualAward);
      if (response.status_code == 200) {
        return response.data;
      } else {
        throw response;
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  }

  async getAllLoginsByCompany() {
    let response = await this.cvApiService.get("/api/v1/manualawards/getAllLoginsByCompany");
    if (response.status_code < 200 || response.status_code >= 300) {
      throw response;
    }
    return response.data;
  };

  async getAllProfileDTOs(): Promise<ProfileDTO[]> {
    let response = await this.cvApiService.get<ProfileDTO[]>("/api/v1/manualawards/getAllProfiles");
    if (response.status_code < 200 || response.status_code >= 300) {
      throw response;
    }
    return response.data;
  };
}
